import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { useStripeAccount } from "@/hooks/useStripeAccount";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { isMobileApp } from "@/lib/platform";

export default function PayoutSetup() {
  const { data: stripeAccount, isLoading, refetch } = useStripeAccount();
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);

  // Handle Stripe onboarding callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeOnboarding = params.get('stripe_onboarding');
    const stripeRefresh = params.get('stripe_refresh');
    
    if (stripeOnboarding === 'complete') {
      // Automatically check onboarding status
      handleCompleteOnboarding();
      
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete('stripe_onboarding');
      window.history.replaceState({}, '', url.toString());
    } else if (stripeRefresh === 'true') {
      toast({
        title: "Setup expired",
        description: "Your onboarding session expired. Please try again.",
        variant: "destructive",
      });
      
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete('stripe_refresh');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const handleSetupPayouts = async () => {
    setIsCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-stripe-account", {
        body: { isMobileApp: isMobileApp() },
      });

      if (error) throw error;

      if (data.onboardingUrl) {
        // Redirect to Stripe onboarding
        window.location.href = data.onboardingUrl;
      } else {
        throw new Error("No onboarding URL received");
      }
    } catch (error: any) {
      console.error("Error setting up payouts:", error);
      toast({
        title: "Setup failed",
        description: error.message || "Failed to set up payout account",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCompleteOnboarding = async () => {
    setIsCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-onboarding-complete", {
        body: { isMobileApp: isMobileApp() },
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: "Success!",
          description: "Your payout account is now active",
        });
        refetch();
      } else if (data.onboardingUrl) {
        // Still needs onboarding, redirect
        window.location.href = data.onboardingUrl;
      }
    } catch (error: any) {
      console.error("Error checking onboarding:", error);
      toast({
        title: "Check failed",
        description: error.message || "Failed to check onboarding status",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasAccount = !!stripeAccount?.stripe_account_id;
  const onboardingComplete = stripeAccount?.stripe_onboarding_complete;
  const payoutsEnabled = stripeAccount?.stripe_payouts_enabled;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Payout Account
              {payoutsEnabled && (
                <Badge variant="default" className="bg-green-600">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Active
                </Badge>
              )}
              {hasAccount && !onboardingComplete && (
                <Badge variant="secondary">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Setup Required
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              {!hasAccount && "Connect your bank account to receive payouts"}
              {hasAccount && !onboardingComplete && "Complete your account setup to receive payouts"}
              {payoutsEnabled && "Your account is ready to receive payouts"}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasAccount && (
          <Button
            onClick={handleSetupPayouts}
            disabled={isCreating}
            className="w-full"
          >
            {isCreating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Setting up...
              </>
            ) : (
              <>
                <ExternalLink className="w-4 h-4 mr-2" />
                Setup Payout Account
              </>
            )}
          </Button>
        )}

        {hasAccount && !onboardingComplete && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              You've started the setup process. Complete your Stripe onboarding to start receiving payouts.
            </p>
            <Button
              onClick={handleCompleteOnboarding}
              disabled={isCreating}
              className="w-full"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Continue Setup
                </>
              )}
            </Button>
          </div>
        )}

        {payoutsEnabled && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Your payout account is fully configured. Earnings will be automatically transferred to your connected bank account.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
